import type { CampaignConfig } from "./types.js";
import {
  commonVisualAssetFiles,
  createCampaignLocalAssetSet,
  createCampaignVisualConfigFromManifest,
  defineCampaignVisualAssetFiles,
  withBase,
} from "./assets.js";
import { commonAuthoredFields } from "./common-fields.js";
import { buddhaSkills } from "./skills.js";
import { createSharedGridMap, createWheelMandalaLandMask } from "./shared-grid.js";

const visualAssets = defineCampaignVisualAssetFiles({
  ...commonVisualAssetFiles,
  mapBackground: "story_intro_1.png",
});

const buddhaLocalAssetFiles = createCampaignLocalAssetSet(visualAssets);

function samsaraTerrain(): {
  path: string;
  buildable: string;
  forest: string;
  rocks: string;
} {
  const base = "/campaigns/buddha/terrain/courtyard";
  return {
    path: withBase(`${base}/path`),
    buildable: withBase(`${base}/buildable`),
    forest: withBase(`${base}/rocks`),
    rocks: withBase(`${base}/rocks`),
  };
}

const buddhaMap = createSharedGridMap({
  authoredFields: commonAuthoredFields,
  landMask: createWheelMandalaLandMask(),
});

const baseVisual = createCampaignVisualConfigFromManifest({
  campaignId: "buddha",
  files: visualAssets,
  campaignLocalFiles: buddhaLocalAssetFiles,
  terrainTileBases: samsaraTerrain(),
});

const visual: CampaignConfig["visual"] = {
  ...baseVisual,
  buildings: {
    Tower: {
      ...baseVisual.buildings.Tower,
      battle: {
        scaleX: 1.16,
        scaleY: 1.46,
        anchorY: 0.66,
        haloColor: "rgba(230, 184, 68, 0.28)",
        haloScale: 1.25,
        shadowAlpha: 0.42,
      },
    },
    Wall: {
      ...baseVisual.buildings.Wall,
      battle: {
        scaleX: 1.08,
        scaleY: 1.08,
        anchorY: 0.54,
        haloColor: "rgba(70, 168, 142, 0.1)",
        haloScale: 0.82,
        shadowAlpha: 0.32,
      },
    },
    Amplifier: {
      ...baseVisual.buildings.Amplifier,
      battle: {
        scaleX: 1.12,
        scaleY: 1.32,
        anchorY: 0.62,
        haloColor: "rgba(76, 218, 196, 0.24)",
        haloScale: 1.35,
        shadowAlpha: 0.34,
      },
    },
    Trap: {
      ...baseVisual.buildings.Trap,
      battle: {
        scaleX: 1.08,
        scaleY: 1.08,
        anchorY: 0.5,
        haloColor: "rgba(230, 184, 68, 0.18)",
        haloScale: 0.92,
        shadowAlpha: 0.22,
      },
    },
    Shrine: {
      ...baseVisual.buildings.Shrine,
      battle: {
        scaleX: 1.18,
        scaleY: 1.38,
        anchorY: 0.62,
        haloColor: "rgba(255, 238, 182, 0.3)",
        haloScale: 1.35,
        shadowAlpha: 0.38,
      },
    },
  },
  pathEndpoints: {
    ...baseVisual.pathEndpoints,
    battle: {
      start: {
        scale: 1.62,
        glowScale: 1.55,
        glowColor: "rgba(232, 80, 42, 0.34)",
        ringColor: "rgba(255, 128, 54, 0.76)",
        particleColor: "rgba(255, 160, 70, 0.82)",
      },
      end: {
        scale: 1.42,
        glowScale: 1.45,
        glowColor: "rgba(250, 236, 178, 0.34)",
        ringColor: "rgba(255, 230, 154, 0.76)",
        particleColor: "rgba(255, 248, 210, 0.82)",
      },
    },
  },
};

export const buddhaCampaign: CampaignConfig = {
  id: "buddha",
  title: "Buddha Defense",
  storageKey: "buddha_defense_save",
  tutorialStorageKey: "buddha_defense_tutorial_done",
  map: buddhaMap,
  story: {
    initialBeatId: "intro",
    fieldWinBeats: {},
    beats: {
      intro: [
        { imageSrc: visualAssets.storyIntro, textKey: "buddha.story.intro.panel1" },
      ],
    },
  },
  visual,
  ui: {
    brandTitle: "Buddha Defense",
    brandIcon: "B",
    mageIcon: "M",
    mageLabelKey: "buddha.hud.monk",
    resourceLabelKey: "buddha.hud.karma",
    waveTypeCodes: {
      Normal: "M",
      Swarm: "S",
      Armored: "G",
      Giant: "O",
      Runner: "W",
    },
  },
  audio: {
    tracks: {
      map: withBase("/campaigns/buddha/audio/map-shakuhachi-solo.mp3"),
      battle: withBase("/campaigns/buddha/audio/battle-zen-beat-2.mp3"),
      win: withBase("/assets/audio/wintheme.wav"),
    },
  },
  skills: buddhaSkills,
};
